import React, { useMemo } from 'react';
import moment from 'moment';

const DAY_TYPES = {
  full: { label: 'Full Day' },
  first: { label: '1st Half' },
  second: { label: '2nd Half' }
};

const getDayTone = (detail = {}) => {
  const raw = String(detail?.leaveDayType || '')
    .trim()
    .toLowerCase();
  if (raw === 'first half' || raw === '1st half') return 'first';
  if (raw === 'second half' || raw === '2nd half') return 'second';
  if (raw === 'full day') return 'full';
  if (detail?.isHalfDay) return Number(detail?.leaveSession) === 2 ? 'second' : 'first';
  return raw ? 'other' : 'full';
};

const getDayLabel = (detail, tone) =>
  DAY_TYPES[tone]?.label || String(detail?.leaveDayType || '').trim() || 'Other';

const LeaveDayBreakdown = ({ leaveDetails }) => {
  const days = useMemo(() => {
    const list = Array.isArray(leaveDetails) ? leaveDetails : [];
    return list
      .filter((d) => d?.date)
      .map((d) => {
        const m = moment.utc(Number(d.date));
        const isWeekend = m.day() === 0 || m.day() === 6;
        const tone = isWeekend ? 'weekend' : getDayTone(d);
        return {
          key: d?._id || String(d.date),
          date: Number(d.date),
          dow: m.format('ddd'),
          day: m.format('DD'),
          monthKey: m.format('YYYY-MM'),
          monthLabel: m.format('MMMM YYYY'),
          isWeekend,
          tone,
          label: isWeekend ? 'Weekend' : getDayLabel(d, tone)
        };
      })
      .sort((a, b) => a.date - b.date);
  }, [leaveDetails]);

  const summary = useMemo(() => {
    const map = new Map();
    days.forEach((d) => {
      const prev = map.get(d.label) || { label: d.label, tone: d.tone, days: 0 };
      map.set(d.label, { ...prev, days: prev.days + 1 });
    });
    return Array.from(map.values()).sort(
      (a, b) => Number(a.tone === 'weekend') - Number(b.tone === 'weekend')
    );
  }, [days]);

  const months = useMemo(() => {
    const groups = [];
    days.forEach((d) => {
      let group = groups.find((item) => item.key === d.monthKey);
      if (!group) {
        group = { key: d.monthKey, label: d.monthLabel, days: [] };
        groups.push(group);
      }
      group.days.push(d);
    });
    return groups;
  }, [days]);

  if (!days.length) return null;

  return (
    <section className="leave-breakdown">
      <div className="leave-breakdown__head">
        <div className="leave-breakdown__title">
          <span className="leave-view__label mb-0">Leave breakdown</span>
          <span className="leave-breakdown__count">
            {months.length} {months.length === 1 ? 'month' : 'months'}
          </span>
        </div>
        <div className="leave-breakdown__summary">
          {summary.map((s) => (
            <span
              key={s.label}
              className={`leave-breakdown__chip leave-breakdown__chip--${s.tone}`}
            >
              {s.label}
              <strong>× {s.days}</strong>
            </span>
          ))}
        </div>
      </div>

      <div className="leave-breakdown__months">
        {months.map((month) => (
          <div key={month.key} className="leave-breakdown__month">
            <div className="leave-breakdown__month-head">
              <strong>{month.label}</strong>
              <span>
                {month.days.length} {month.days.length === 1 ? 'day' : 'days'}
              </span>
            </div>
            <div className="leave-breakdown__days">
              {month.days.map((d) => (
                <span
                  key={d.key}
                  className={`leave-breakdown__token leave-breakdown__token--${d.tone}`}
                  title={`${d.dow} ${d.day} · ${d.label}`}
                >
                  <em>{d.dow}</em>
                  {d.day}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default LeaveDayBreakdown;
